import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchHero from './SearchHero';
import SearchControls from '../SearchControls';
import Recents from '../Recents';
import HomeScreenContent from './HomeScreenContent';
import { SearchControlsProps, RecentSearch } from '../../types/search';
import { serializeSearchParams } from '../../utils/urlParams';

const HomeScreen: React.FC<SearchControlsProps> = ({
  setOrigin,
  setDestination,
  setOriginName,
  setDestinationName,
  setDate,
  setTripType,
  setOptions,
  handleFetchJourneys,
  recentSearches,
  originId,
  destinationId,
  originName,
  destinationName,
  date,
  tripType,
  options,
}) => {
  const navigate = useNavigate();
  const [shouldFetch, setShouldFetch] = useState(false);

  // Effect to trigger fetch when shouldFetch is true
  useEffect(() => {
    if (shouldFetch) {
      // Only proceed if we have valid origin and destination
      if (!originId || !destinationId) {
        console.warn('Cannot fetch journeys: missing origin or destination');
        setShouldFetch(false);
        return;
      }
      
      // Call fetchJourneys here with updated parameters
      handleFetchJourneys('initial');
      // Navigate to journeys page with URL parameters
      const searchDate = date || new Date().toISOString();
      const params = serializeSearchParams(
        originId,
        destinationId,
        originName,
        destinationName,
        searchDate,
        tripType,
        options
      );
      navigate(`/journeys?${params.toString()}`, { state: { from: '/' } });
      // Reset the fetch trigger
      setShouldFetch(false);
    }
  }, [shouldFetch, handleFetchJourneys, navigate, originId, destinationId, originName, destinationName, date, tripType, options]);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function to handle clicking on a recent search
  const handleRecentSearch = (search: RecentSearch) => {
    // Update state based on recent search
    setOrigin(search.originId);
    setDestination(search.destinationId);
    setOriginName(search.originName);
    setDestinationName(search.destinationName);
    setDate(search.date);
    setTripType(search.tripType as 'departure' | 'arrival');
    setOptions(search.options);

    // Trigger the fetch
    setShouldFetch(true);
  };

  return (
    <>
      <SearchHero
        setOrigin={setOrigin}
        setDestination={setDestination}
        setOriginName={setOriginName}
        setDestinationName={setDestinationName}
        setDate={setDate}
        setTripType={setTripType}
        setOptions={setOptions}
        fetchJourneys={() => setShouldFetch(true)}
        originId={originId}
        destinationId={destinationId}
        originName={originName}
        destinationName={destinationName}
        date={date}
        tripType={tripType}
        options={options}
      />
      {/* Conditionally render Recents component only if there are recent searches */}
      {recentSearches.length > 0 && (
        <Recents recentSearches={recentSearches} onSearch={handleRecentSearch} />
      )}
      <HomeScreenContent/>
    </>
  );
};

export default HomeScreen;
